import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router';
import { Skeleton } from '@mui/material';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { SidebarTradeCard } from 'screens/Public/UserProfile/context/ProfileTradeSidebarContext';
import {
  useGetTradeAlertsQuery,
  useHideTradeAlertMutation,
  useUnhideTradeAlertMutation,
  TradeAlert,
  TradeAlertsTab,
  TradeAlertsAlertStatus,
  TradeAlertsSort,
} from 'store/services/tradeAlerts/tradeAlertsApi';
import { PriceRangeFilter } from 'components/PriceRangeFilter/PriceRangeFilter';
import CardSizeSelector, { CardSize } from 'components/CardSizeSelector/CardSizeSelector';
import AlertCard from './components/AlertCard';
import AlertTradeGroup, { AlertGroup } from './components/AlertTradeGroup';
import ComposeTradeModal, { ComposeTradeRecipient } from './components/ComposeTradeModal/ComposeTradeModal';
import './TradeAlertsPage.css';

const PRICE_DEBOUNCE_MS = 300;

const POLL_INTERVAL_MS = 30000;

/** View mode: a flat chronological card feed vs. cards grouped per requester. */
type TradeAlertsViewMode = 'by-card' | 'by-trader';

// Collapse alerts into per-requester groups. Groups are ordered by their most-recent
// alert (desc) so a fresh alert from an already-seen trader resurfaces the group to the
// top; within a group the alerts keep the backend's active sort order.
const groupAlertsByRequester = (alerts: TradeAlert[]): AlertGroup[] => {
  const byId = new Map<number, AlertGroup>();
  const order: number[] = [];
  for (const alert of alerts) {
    const id = alert.requester.id;
    let group = byId.get(id);
    if (!group) {
      group = {
        requester: alert.requester,
        alerts: [],
        count: 0,
        totalPoints: 0,
        mostRecent: alert.availableSince,
      };
      byId.set(id, group);
      order.push(id);
    }
    group.alerts.push(alert);
    group.count += 1;
    group.totalPoints += alert.pointValue;
    if (alert.availableSince > group.mostRecent) group.mostRecent = alert.availableSince;
  }
  return order
    .map((id) => byId.get(id) as AlertGroup)
    .sort((a, b) => b.mostRecent.localeCompare(a.mostRecent));
};

const SORT_OPTIONS: { value: TradeAlertsSort; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'highest-value', label: 'Highest value' },
  { value: 'lowest-value', label: 'Lowest value' },
];

// Map an alert to the SidebarTradeCard shape the ComposeTradeModal pre-stages.
// The wanter's acceptance criteria (when present on the alert card) flow through so the
// modal's version flyout shows real set/finish/condition/language options; when absent we
// fall back to the single matched Have printing.
const alertToStagedCard = (alert: TradeAlert): SidebarTradeCard => {
  const { card } = alert;
  return {
    id: `alert-${alert.id}`,
    wantListId: alert.wantListItemId,
    cardId: card.id,
    cardName: card.name,
    set: card.setCode,
    setCode: card.setCode,
    condition: card.condition,
    language: card.language,
    foil: card.foil,
    imageUrl: card.imageUrl,
    pointValue: alert.pointValue,
    scryfallId: card.scryfallId,
    acceptFoil: card.acceptFoil ?? true,
    acceptNonFoil: card.acceptNonFoil ?? true,
    availableInFoil: card.availableInFoil ?? card.foil,
    availableInNonFoil: card.availableInNonFoil ?? !card.foil,
    acceptableSetDetails: card.acceptableSetDetails,
    acceptableConditions: card.acceptableConditions,
    acceptableLanguages: card.acceptableLanguages,
    availableLanguages: card.availableLanguages,
    maxQuantity: 1,
  };
};

interface ComposeState {
  recipient: ComposeTradeRecipient;
  stagedCard: SidebarTradeCard;
}

const TradeAlertsPage: React.FC = () => {
  const [tab, setTab] = useState<TradeAlertsTab>('all');
  // Two independent filter axes: alert-status (mutually exclusive) and price range (composable min/max).
  const [alertStatus, setAlertStatus] = useState<TradeAlertsAlertStatus>('all');
  const [priceMin, setPriceMin] = useState<number | undefined>(undefined);
  const [priceMax, setPriceMax] = useState<number | undefined>(undefined);
  const [sort, setSort] = useState<TradeAlertsSort>('newest');
  // View mode is session-local (resets on full refresh, like the filters). Default by-card.
  const [viewMode, setViewMode] = useState<TradeAlertsViewMode>('by-card');
  // Card display size — session-local (resets to M on full refresh), not persisted.
  const [cardSize, setCardSize] = useState<CardSize>('M');
  const [isPaused, setIsPaused] = useState(false);
  const [compose, setCompose] = useState<ComposeState | null>(null);

  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  // Debounce the price inputs so we don't refetch on every keystroke (mirrors the Send page).
  const [debouncedPriceMin, setDebouncedPriceMin] = useState<number | undefined>(undefined);
  const [debouncedPriceMax, setDebouncedPriceMax] = useState<number | undefined>(undefined);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPriceMin(priceMin);
      setDebouncedPriceMax(priceMax);
    }, PRICE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [priceMin, priceMax]);

  const { data, isLoading, isFetching, refetch } = useGetTradeAlertsQuery(
    { tab, sort, alertStatus, priceMin: debouncedPriceMin, priceMax: debouncedPriceMax },
    { pollingInterval: isPaused ? 0 : POLL_INTERVAL_MS },
  );

  const [hideTradeAlert] = useHideTradeAlertMutation();
  const [unhideTradeAlert] = useUnhideTradeAlertMutation();

  // Close sort dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) setIsSortOpen(false);
    };
    if (isSortOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSortOpen]);

  const alerts = useMemo(() => data?.alerts ?? [], [data?.alerts]);
  const groups = useMemo(
    () => (viewMode === 'by-trader' ? groupAlertsByRequester(alerts) : []),
    [viewMode, alerts],
  );
  const hiddenCount = data?.hiddenCount ?? 0;
  // The three status pills read their counts from `counts` (reflects tab + price range,
  // independent of the selected status). Hidden pill uses hiddenCount.
  const counts = data?.counts ?? { all: 0, on: 0, off: 0 };

  const handleResume = () => {
    setIsPaused(false);
    refetch();
  };

  const handleSend = (alert: TradeAlert) => {
    setCompose({
      recipient: { id: alert.requester.id, username: alert.requester.username },
      stagedCard: alertToStagedCard(alert),
    });
  };

  const handleHide = (alert: TradeAlert) => {
    hideTradeAlert({ haveListItemId: alert.haveListItemId, wantListItemId: alert.wantListItemId });
  };

  const handleUnhide = (alert: TradeAlert) => {
    unhideTradeAlert({ haveListItemId: alert.haveListItemId, wantListItemId: alert.wantListItemId });
  };

  const currentSortLabel = SORT_OPTIONS.find(o => o.value === sort)?.label || 'Newest';
  const showEmpty = !isLoading && alerts.length === 0;
  // Drives per-tile scaling in both views (column count in by-card, --tile width in by-trader).
  const sizeClass = `size-${cardSize.toLowerCase()}`;

  return (
    <>
      <SEOHelmet
        title="Trade Alerts - TradeMagic"
        description="Other traders want these cards and you have them. Send to complete the trade."
        canonical="https://trademagic.gg/trade-alerts"
      />
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg' },
          { name: 'Trade Alerts', url: 'https://trademagic.gg/trade-alerts' },
        ]}
      />

      <div className="trade-alerts-page">
        {/* Header */}
        <div className="trade-alerts-header">
          <h1 className="trade-alerts-title">Trade Alerts</h1>
          <p className="trade-alerts-subtitle">
            Other traders want these cards and you have them. Send to complete the trade.
          </p>
        </div>

        {/* Controls row */}
        <div className="trade-alerts-controls">
          <div className="trade-alerts-controls-left">
            {/* Alert-status axis (mutually exclusive). Independent of the Hidden tab. */}
            <button
              className={`trade-alerts-pill ${alertStatus === 'all' ? 'active' : ''}`}
              onClick={() => setAlertStatus('all')}
            >
              All · {counts.all}
            </button>
            <button
              className={`trade-alerts-pill ${alertStatus === 'on' ? 'active' : ''}`}
              onClick={() => setAlertStatus('on')}
            >
              Alerts on · {counts.on}
            </button>
            <button
              className={`trade-alerts-pill ${alertStatus === 'off' ? 'active' : ''}`}
              onClick={() => setAlertStatus('off')}
            >
              Alerts off · {counts.off}
            </button>
            {/* Price-range axis (composable min/max). Empty inputs = no filtering. */}
            <PriceRangeFilter
              priceMin={priceMin}
              priceMax={priceMax}
              onPriceMinChange={setPriceMin}
              onPriceMaxChange={setPriceMax}
            />

            <div className="trade-alerts-sort" ref={sortRef}>
              <button
                type="button"
                className={`trade-alerts-sort-trigger ${isSortOpen ? 'open' : ''}`}
                onClick={() => setIsSortOpen(o => !o)}
              >
                <span>{currentSortLabel}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {isSortOpen && (
                <div className="trade-alerts-sort-menu" role="listbox">
                  {SORT_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      className={`trade-alerts-sort-option ${sort === option.value ? 'selected' : ''}`}
                      onClick={() => { setSort(option.value); setIsSortOpen(false); }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Card display size (S/M/L) — session-local, near the sort dropdown. */}
            <CardSizeSelector value={cardSize} onChange={setCardSize} />
          </div>

          <div className="trade-alerts-controls-right">
            {/* View mode: flat card feed vs. per-trader groups. Session-local. */}
            <div className="trade-alerts-viewmode" role="group" aria-label="View mode">
              <button
                className={`trade-alerts-pill ${viewMode === 'by-card' ? 'active' : ''}`}
                onClick={() => setViewMode('by-card')}
              >
                By card
              </button>
              <button
                className={`trade-alerts-pill ${viewMode === 'by-trader' ? 'active' : ''}`}
                onClick={() => setViewMode('by-trader')}
              >
                By trader
              </button>
            </div>

            <button
              className={`trade-alerts-pill ${tab === 'hidden' ? 'active' : ''}`}
              onClick={() => setTab(t => (t === 'hidden' ? 'all' : 'hidden'))}
            >
              Hidden · {hiddenCount}
            </button>

            <div className={`trade-alerts-live ${isPaused ? 'paused' : ''}`}>
              <span className="trade-alerts-live-dot" />
              <span>{isPaused ? 'Paused' : 'Live'}</span>
            </div>
            <button
              className="trade-alerts-pause"
              onClick={() => (isPaused ? handleResume() : setIsPaused(true))}
            >
              {isPaused ? 'Resume' : 'Pause'}
            </button>
          </div>
        </div>

        {/* Grid / states */}
        {isLoading ? (
          <div className={`trade-alerts-grid ${sizeClass}`}>
            {[...Array(10)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                sx={{ backgroundColor: 'rgba(255,255,255,0.08)', height: '360px', borderRadius: '12px' }}
              />
            ))}
          </div>
        ) : showEmpty ? (
          <div className="trade-alerts-empty">
            {tab === 'hidden' ? (
              <p>No hidden alerts.</p>
            ) : (
              <p>
                No trade alerts right now. Enable alerts on more cards in your{' '}
                <Link to="/my-cards/have-list">Have List</Link> to catch more opportunities.
              </p>
            )}
          </div>
        ) : viewMode === 'by-trader' ? (
          <div className={`trade-alerts-groups ${sizeClass} ${isFetching ? 'is-fetching' : ''}`}>
            {groups.map(group => (
              <AlertTradeGroup
                key={group.requester.id}
                group={group}
                variant={tab}
                onSend={handleSend}
                onHide={handleHide}
                onUnhide={handleUnhide}
              />
            ))}
          </div>
        ) : (
          <div className={`trade-alerts-grid ${sizeClass} ${isFetching ? 'is-fetching' : ''}`}>
            {alerts.map(alert => (
              <AlertCard
                key={alert.id}
                alert={alert}
                variant={tab}
                onSend={handleSend}
                onHide={handleHide}
                onUnhide={handleUnhide}
              />
            ))}
          </div>
        )}
      </div>

      {compose && (
        <ComposeTradeModal
          isOpen={true}
          onClose={() => setCompose(null)}
          recipient={compose.recipient}
          initialStagedCard={compose.stagedCard}
        />
      )}
    </>
  );
};

export default TradeAlertsPage;
